import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Card } from '../cards/Cards.tsx';
import { IconArrowRight, IconArrowUp, IconSparkles, IconStop } from '../ui/Icons.tsx';
import type { ChatItem } from '../../lib/useRun.ts';
import type { ConnectionState } from '../../lib/agentClient.ts';

const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * A handoff is transport, not conversation — the user must never see a reconnect. `handoff`
 * and `streaming` therefore render the identical "Working" affordance; only a real network
 * retry admits that anything went wrong.
 */
export function StatusLine({ conn }: { conn: ConnectionState }) {
  if (conn.phase === 'idle' || conn.phase === 'done') return null;

  const failed = conn.phase === 'failed';
  const label =
    conn.phase === 'streaming' || conn.phase === 'handoff'
      ? 'Working'
      : conn.phase === 'retrying'
        ? `Reconnecting · attempt ${conn.attempt}`
        : 'Failed';

  return (
    <motion.div
      className="lx-status"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
    >
      <span className="lx-ping" data-state={failed ? 'failed' : 'live'}>
        <i />
        <em />
      </span>
      <span className={failed ? undefined : 'lx-thinking'}>{label}</span>
      {failed && <span style={{ color: 'var(--lx-err)' }}>{conn.error}</span>}
    </motion.div>
  );
}

function Message({
  item,
  onDecide,
}: {
  item: ChatItem;
  onDecide: (id: string, d: 'approve' | 'reject') => void;
}) {
  const isUser = item.role === 'user';
  return (
    <motion.div
      className={`lx-msg${isUser ? ' lx-msg-user' : ''}`}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: EASE }}
    >
      {isUser ? (
        <div className="lx-bubble">{item.text}</div>
      ) : (
        <>
          <span className="lx-role">
            <span className="lx-dot-mark">L</span>
            Lumlix
          </span>
          {item.text && (
            <div className="lx-prose">
              {item.text}
              {item.streaming && <span className="lx-caret" />}
            </div>
          )}
          {item.cards.map((c, i) => (
            <Card key={i} card={c} onDecide={onDecide} />
          ))}
        </>
      )}
    </motion.div>
  );
}

export function Chat({
  items,
  conn,
  onSend,
  onDecide,
  onStop,
}: {
  items: ChatItem[];
  conn: ConnectionState;
  onSend: (msg: string) => void;
  onDecide: (id: string, d: 'approve' | 'reject') => void;
  onStop?: () => void;
}) {
  const [draft, setDraft] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const stickRef = useRef(true);
  const busy = conn.phase === 'streaming' || conn.phase === 'handoff' || conn.phase === 'retrying';

  // Follow the stream only while the user is already at the bottom. Yanking them back down
  // mid-scroll while tokens arrive is the single most irritating thing a chat UI can do.
  const onScroll = useCallback(() => {
    const el = threadRef.current;
    if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  useLayoutEffect(() => {
    const el = threadRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [items, conn.phase]);

  // Auto-grow the composer, capped by CSS max-height so it scrolls instead of eating the view.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }, [draft]);

  function submit() {
    const v = draft.trim();
    if (!v || busy) return;
    setDraft('');
    stickRef.current = true;
    onSend(v);
  }

  return (
    <>
      <div className="lx-thread lx-scroll" ref={threadRef} onScroll={onScroll}>
        <div className="lx-thread-inner" data-empty={items.length === 0}>
          {items.length === 0 && <EmptyState onPick={onSend} />}
          <AnimatePresence initial={false}>
            {items.map((it) => (
              <Message key={it.id} item={it} onDecide={onDecide} />
            ))}
          </AnimatePresence>
          <AnimatePresence>
            <StatusLine conn={conn} />
          </AnimatePresence>
        </div>
      </div>

      <div className="lx-composer-wrap">
        <div className="lx-composer">
          <textarea
            ref={taRef}
            className="lx-focus"
            value={draft}
            rows={1}
            placeholder="Describe the outcome you want…"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          {busy && onStop ? (
            <button className="lx-send lx-focus" data-stop="true" onClick={onStop} aria-label="Stop">
              <IconStop size={16} />
            </button>
          ) : (
            <button
              className="lx-send lx-focus"
              onClick={submit}
              disabled={!draft.trim()}
              aria-label="Send message"
            >
              <IconArrowUp size={17} />
            </button>
          )}
        </div>
        <p className="lx-hint">
          Lumlix asks before it spends money or publishes anything. Enter to send, Shift+Enter for a new line.
        </p>
      </div>
    </>
  );
}

const PROMPTS = [
  'Launch my new product and generate qualified leads',
  'Review last week’s ad performance and fix what’s underperforming',
  'Plan and schedule this month’s YouTube uploads',
];

function EmptyState({ onPick }: { onPick: (s: string) => void }) {
  return (
    <div className="lx-hero">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
      >
        <span className="lx-eyebrow" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <IconSparkles size={13} /> Your marketing agent
        </span>
        <h1 style={{ marginTop: 10 }}>
          Describe the outcome.
          <br />
          <span className="lx-grad-text">Lumlix runs the campaign.</span>
        </h1>
        <p>
          It plans the strategy, writes the creative, and builds the platform payloads — then
          stops and asks you before it spends anything.
        </p>
      </motion.div>

      <div className="lx-suggest">
        {PROMPTS.map((p, i) => (
          <motion.button
            key={p}
            className="lx-focus"
            onClick={() => onPick(p)}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.12 + i * 0.07, ease: EASE }}
          >
            <span>{p}</span>
            <IconArrowRight size={16} />
          </motion.button>
        ))}
      </div>
    </div>
  );
}
